import React from 'react';
import { useTranslation } from 'react-i18next';
import { LogOut, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { NotificationBell } from './NotificationBell';
import { NotificationCenter } from '@/features/notifications/components/NotificationCenter';

export interface ProfileWidgetProps {
  username: string;
  role: string;
  initials?: string;
  onLogout?: () => void;
  className?: string;
}

/**
 * ProfileWidget Component v2 - FacturXPro Design System
 *
 * Widget profil flottant (desktop only) avec avatar et bouton logout
 * Utilisé en haut à droite du Dashboard
 *
 * @example
 * ```tsx
 * <ProfileWidget
 *   username="John Doe"
 *   role="Administrateur"
 *   initials="JD"
 *   onLogout={() => handleLogout()}
 * />
 * ```
 *
 * Référence: DESIGN_SYSTEM.md section "Profile Widget"
 * Source: DashboardOverview.tsx floating profile widget
 */
export const ProfileWidget: React.FC<ProfileWidgetProps> = ({
  username,
  role,
  initials,
  onLogout,
  className = '',
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Generate initials if not provided
  const displayInitials =
    initials ||
    username
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);

  return (
    <>
      <div
        className={`hidden md:flex fixed top-6 right-6 z-50 items-center gap-3 ${className}`}
      >
        {/* User Profile Capsule - Clickable to go to account settings */}
        <button
          onClick={() => navigate('/settings/account')}
          className="bg-white/60 backdrop-blur-xl rounded-full px-5 py-2.5 shadow-lg border border-white/60 flex items-center gap-3 hover:bg-white/70 transition-all duration-300 cursor-pointer"
        >
          <div className="w-9 h-9 bg-gradient-to-br from-teal-600 to-teal-700 rounded-full flex items-center justify-center shadow-md">
            <span className="text-white text-sm font-bold">{displayInitials}</span>
          </div>
          <div className="text-sm text-left">
            <p className="font-bold text-gray-900">{username}</p>
            <p className="text-xs text-gray-600 font-medium">{role}</p>
          </div>
        </button>

        {/* Account Settings Button */}
        <button
          onClick={() => navigate('/settings/account')}
          className="bg-white/60 backdrop-blur-xl rounded-full p-3 shadow-lg border border-white/60 hover:bg-teal-50/80 hover:border-teal-200/60 transition-all duration-300 group"
          title={t('common.ui.profileWidget.accountSettings')}
        >
          <Settings className="w-5 h-5 text-gray-700 group-hover:text-teal-600 transition-colors" />
        </button>

        {/* Notification Bell */}
        <NotificationBell />

        {/* Logout Button */}
        {onLogout && (
          <button
            onClick={onLogout}
            className="bg-white/60 backdrop-blur-xl rounded-full p-3 shadow-lg border border-white/60 hover:bg-red-50/80 hover:border-red-200/60 transition-all duration-300 group"
            title={t('common.ui.profileWidget.logout')}
          >
            <LogOut className="w-5 h-5 text-gray-700 group-hover:text-red-600 transition-colors" />
          </button>
        )}
      </div>

      {/* Notification Center Drawer */}
      <NotificationCenter />
    </>
  );
};

export default ProfileWidget;
